import useSWR from 'swr'
import { RegistrantController } from '@/controllers'
import { config } from '@/config'
import { Registrant, SavingAddressDTO, UpdatingDTO } from '@/interfaces/registrant'
import { useAuth } from '@/hooks/useAuth'

export const useRegistrant = () => {
  const {
    data,
    error: uncaughtError,
    isLoading,
    mutate,
    isValidating,
  } = useSWR(config.api.endpoints.registrant.me, RegistrantController.me)
  const registrant = data?.result?.[1] || null
  const error = data?.result?.[0] || uncaughtError
  const { authorize, logout } = useAuth()

  if (error) {
    const code = data?.code || 500

    if (code) {
      if (code > 400 && code < 500) {
        logout()
      }
    }
  }

  const changeOrganization = async (orgId: number) => {
    const {
      result: [error, registrant],
      token,
    } = await RegistrantController.changeOrganization(orgId)
    if (error) {
      return [error, null]
    }

    authorize(token!, registrant.id)

    return [null, registrant]
  }

  const revalidate = async () => {
    const response = await mutate()

    if (!response) {
      throw new Error('Failed to revalidate registrant')
    }

    return response.result
  }

  const update = async (dto: UpdatingDTO) => {
    const { result } = await RegistrantController.update(dto)
    const updatedRegistrant = result[1]

    if (updatedRegistrant) {
      updateRegistrantState(updatedRegistrant)
    }

    return result
  }

  const saveAddress = async (dto: SavingAddressDTO) => {
    const { result } = await RegistrantController.saveAddress(dto)
    const updatedRegistrant = result[1]

    if (updatedRegistrant) {
      updateRegistrantState(updatedRegistrant)
    }

    return result
  }

  const updateRegistrantState = (registrant: Registrant) => {
    if (!data) {
      return
    }

    void mutate(
      {
        ...data,
        result: [null, registrant],
      },
      { revalidate: false },
    )
  }

  return {
    registrant,
    error,
    isLoading,
    isValidating,
    revalidate,
    changeOrganization,
    update,
    saveAddress,
  }
}
